import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import LeaveBalanceService from '../services/leaveBalanceService';
import LeaveTypeService from '../services/leaveTypeService';

const LeaveBalanceAssignmentPage = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Assignment Form State
  const [assignmentForm, setAssignmentForm] = useState({
    userId: '',
    leaveTypeId: '',
    totalLeaves: '',
    year: new Date().getFullYear()
  });

  // Employee search state
  const [employeeSearch, setEmployeeSearch] = useState('');

  const normalizeEmployee = (employee = {}) => {
    // Handle null or undefined employee
    if (!employee) {
      return {
        _id: null,
        displayName: 'Unknown Employee'
      };
    }

    const source = employee?.userId && typeof employee.userId === 'object'
      ? employee.userId
      : employee;

    const fullNameFromParts = [source?.firstName, source?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    const displayName =
      source?.name ||
      source?.fullName ||
      fullNameFromParts ||
      (source?.email ? source.email.split('@')[0] : '') ||
      'Unknown Employee';

    return {
      ...source,
      _id: source?._id || source?.id,
      displayName
    };
  };

  const getEmployeeDisplayName = (employee = {}) => {
    // Handle null or undefined employee
    if (!employee) {
      return 'Unknown Employee';
    }
    const normalized = normalizeEmployee(employee);
    return normalized.displayName;
  };

  const getEmployeeOptionLabel = (employee = {}) => {
    if (!employee) return 'Unknown Employee';
    const displayName = getEmployeeDisplayName(employee);
    const extras = [employee.employeeId, employee.email].filter(Boolean).join(' - ');
    return extras ? `${displayName} - ${extras}` : displayName;
  };

  // Filter employees based on search
  const filteredEmployees = employees.filter(employee =>
    getEmployeeDisplayName(employee).toLowerCase().includes(employeeSearch.toLowerCase()) ||
    (normalizeEmployee(employee).employeeId && normalizeEmployee(employee).employeeId.toLowerCase().includes(employeeSearch.toLowerCase())) ||
    (normalizeEmployee(employee).phoneNumber && normalizeEmployee(employee).phoneNumber.toLowerCase().includes(employeeSearch.toLowerCase())) ||
    (normalizeEmployee(employee).email && normalizeEmployee(employee).email.toLowerCase().includes(employeeSearch.toLowerCase()))
  );

  // Bulk Assignment State
  const [bulkAssignments, setBulkAssignments] = useState([]);
  const [showBulkAssignment, setShowBulkAssignment] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchLeaveTypes();
    fetchLeaveBalances();
  }, [selectedYear]);

  const fetchEmployees = async () => {
    try {
      // Fetch HR employees using the new leave balance service endpoint
      const response = await LeaveBalanceService.getHREmployees();
      const normalizedEmployees = (response.data || []).map(normalizeEmployee);
      setEmployees(normalizedEmployees);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      setError('Failed to fetch employees');
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await LeaveTypeService.getAllLeaveTypes();
      setLeaveTypes(response.data);
    } catch (error) {
      setError('Failed to fetch leave types');
    }
  };

  const fetchLeaveBalances = async () => {
    try {
      setLoading(true);
      const response = await LeaveBalanceService.getAllLeaveBalances({ year: selectedYear });
      setLeaveBalances(response.data);
    } catch (error) {
      setError('Failed to fetch leave balances');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await LeaveBalanceService.assignLeaveBalance(assignmentForm);

      setAssignmentForm({
        userId: '',
        leaveTypeId: '',
        totalLeaves: '',
        year: new Date().getFullYear()
      });

      fetchLeaveBalances();
    } catch (error) {
      setError(error.error || 'Failed to assign leave balance');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAssignment = async () => {
    try {
      setLoading(true);
      await LeaveBalanceService.bulkAssignLeaveBalances(bulkAssignments);
      setBulkAssignments([]);
      setShowBulkAssignment(false);
      fetchLeaveBalances();
    } catch (error) {
      setError(error.error || 'Failed to bulk assign leave balances');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBalance = async (balanceId) => {
    if (!window.confirm('Are you sure you want to delete this leave balance?')) {
      return;
    }

    try {
      await LeaveBalanceService.deleteLeaveBalance(balanceId);
      fetchLeaveBalances();
    } catch (error) {
      setError(error.error || 'Failed to delete leave balance');
    }
  };

  const handleResetYear = async () => {
    if (!window.confirm(`Are you sure you want to reset all leave balances for ${selectedYear + 1}?`)) {
      return;
    }

    try {
      setLoading(true);
      await LeaveBalanceService.resetLeaveBalancesForNewYear(selectedYear + 1);
      setSelectedYear(selectedYear + 1);
      fetchLeaveBalances();
    } catch (error) {
      setError(error.error || 'Failed to reset leave balances');
    } finally {
      setLoading(false);
    }
  };

  const addBulkAssignment = () => {
    setBulkAssignments(prev => [...prev, {
      userId: '',
      leaveTypeId: '',
      totalLeaves: '',
      year: selectedYear
    }]);
  };

  const updateBulkAssignment = (index, field, value) => {
    setBulkAssignments(prev => prev.map((assignment, i) =>
      i === index ? { ...assignment, [field]: value } : assignment
    ));
  };

  const removeBulkAssignment = (index) => {
    setBulkAssignments(prev => prev.filter((_, i) => i !== index));
  };

  if (!['HRO', 'ADMIN', 'CHAIRMAN'].includes(user?.role)) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="error-message">
            Access denied. Only HRO, Admin, and Chairman can access this page.
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="page-header">
          <h1>Leave Balance Assignment</h1>
          <div className="header-actions">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="year-selector"
            >
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              className="btn btn-secondary"
              onClick={() => setShowBulkAssignment(!showBulkAssignment)}
            >
              {showBulkAssignment ? 'Hide' : 'Show'} Bulk Assignment
            </button>
            <button
              className="btn btn-warning"
              onClick={handleResetYear}
              disabled={loading}
            >
              Reset for {selectedYear + 1}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} className="btn btn-sm btn-secondary">
              Dismiss
            </button>
          </div>
        )}

        {/* Single Assignment Form */}
        {!showBulkAssignment && (
          <div className="assignment-form-section">
            <h2>Assign Leave Balance</h2>

            {/* Employee Search */}
            <div className="employee-search">
              <input
                type="text"
                placeholder="Search employees by name, ID, phone or email..."
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                className="search-input"
              />
            </div>

            <form onSubmit={handleAssignmentSubmit} className="assignment-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="userId">Employee *</label>
                  <select
                    id="userId"
                    value={assignmentForm.userId}
                    onChange={(e) => setAssignmentForm(prev => ({ ...prev, userId: e.target.value }))}
                    required
                  >
                    <option value="">Select Employee</option>
                    {filteredEmployees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {getEmployeeOptionLabel(employee)}
                      </option>
                    ))}
                  </select>
                  {employees.length === 0 && (
                    <small className="text-warning">No HR employees found</small>
                  )}
                  {employees.length > 0 && filteredEmployees.length === 0 && employeeSearch && (
                    <small className="text-warning">No employees match your search</small>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="leaveTypeId">Leave Type *</label>
                  <select
                    id="leaveTypeId"
                    value={assignmentForm.leaveTypeId}
                    onChange={(e) => setAssignmentForm(prev => ({ ...prev, leaveTypeId: e.target.value }))}
                    required
                  >
                    <option value="">Select Leave Type</option>
                    {leaveTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name} (Max: {type.maxLeavesPerYear} days)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="totalLeaves">Total Leaves *</label>
                  <input
                    type="number"
                    id="totalLeaves"
                    value={assignmentForm.totalLeaves}
                    onChange={(e) => setAssignmentForm(prev => ({ ...prev, totalLeaves: parseInt(e.target.value) }))}
                    min="0"
                    max="365"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="year">Year *</label>
                  <input
                    type="number"
                    id="year"
                    value={assignmentForm.year}
                    onChange={(e) => setAssignmentForm(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    min="2020"
                    max="2050"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Assigning...' : 'Assign Leave Balance'}
              </button>
            </form>
          </div>
        )}

        {/* Bulk Assignment Form */}
        {showBulkAssignment && (
          <div className="bulk-assignment-section">
            <h2>Bulk Assignment</h2>

            <div className="bulk-assignment-controls">
              <button
                className="btn btn-secondary"
                onClick={addBulkAssignment}
              >
                Add Assignment
              </button>
              {bulkAssignments.length > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={handleBulkAssignment}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : `Assign ${bulkAssignments.length} Balances`}
                </button>
              )}
            </div>

            {bulkAssignments.map((assignment, index) => (
              <div key={index} className="bulk-assignment-row">
                <div className="form-group">
                  <select
                    value={assignment.userId}
                    onChange={(e) => updateBulkAssignment(index, 'userId', e.target.value)}
                    required
                  >
                    <option value="">Select Employee</option>
                    {filteredEmployees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {getEmployeeOptionLabel(employee)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <select
                    value={assignment.leaveTypeId}
                    onChange={(e) => updateBulkAssignment(index, 'leaveTypeId', e.target.value)}
                    required
                  >
                    <option value="">Select Leave Type</option>
                    {leaveTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <input
                    type="number"
                    value={assignment.totalLeaves}
                    onChange={(e) => updateBulkAssignment(index, 'totalLeaves', parseInt(e.target.value))}
                    placeholder="Total Leaves"
                    min="0"
                    max="365"
                    required
                  />
                </div>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => removeBulkAssignment(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Current Leave Balances */}
        <div className="current-balances-section">
          <h2>Current Leave Balances ({selectedYear})</h2>

          <div className="table-container">
            {loading ? (
              <div className="loading-message">Loading leave balances...</div>
            ) : leaveBalances.length === 0 ? (
              <div className="empty-state">
                <p>No leave balances found for {selectedYear}.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Leave Type</th>
                    <th>Total</th>
                    <th>Used</th>
                    <th>Remaining</th>
                    <th>Utilization</th>
                    <th>Assigned By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveBalances.map((balance) => (
                    <tr key={balance._id}>
                      <td>
                        <div>
                          <div>{getEmployeeDisplayName(balance.userId)}</div>
                          {balance.userId?.employeeId && <small>ID: {balance.userId.employeeId}</small>}
                          {balance.userId?.phoneNumber && <small>{balance.userId.phoneNumber}</small>}
                          {balance.userId?.email && (
                            <small>{balance.userId.email}</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="leave-type-info">
                          <div
                            className="leave-type-indicator"
                            style={{ backgroundColor: balance.leaveTypeId.color }}
                          ></div>
                          {balance.leaveTypeId.name}
                        </div>
                      </td>
                      <td>{balance.totalLeaves}</td>
                      <td>{balance.usedLeaves}</td>
                      <td>{balance.remainingLeaves}</td>
                      <td>
                        <div className="utilization-bar">
                          <div
                            className="utilization-fill"
                            style={{
                              width: `${(balance.usedLeaves / balance.totalLeaves) * 100}%`
                            }}
                          ></div>
                          <span className="utilization-text">
                            {Math.round((balance.usedLeaves / balance.totalLeaves) * 100)}%
                          </span>
                        </div>
                      </td>
                      <td>{getEmployeeDisplayName(balance.assignedBy)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteBalance(balance._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default LeaveBalanceAssignmentPage;